import { useState, type FormEvent } from 'react';
import { Link, useRouter } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft } from 'lucide-react';
import {
  Button,
  Card,
  Field,
  Input,
  Select,
  Switch,
  Textarea,
  backLinkVariants,
} from '@wundo/ui';
import { todayInBrussels } from '@wundo/shared/todayInBrussels.ts';
import { TRACK_LIMITS } from '@wundo/shared/trackLimits.ts';
import { PageHeader } from '@/components/PageHeader.tsx';
import { ReadingColumn } from '@/components/ReadingColumn.tsx';
import {
  createCohort,
  listCohortTrackOptions,
  updateCohort,
  type CohortWriteRequest,
} from '@/lib/cohorts.ts';
import { apiErrorMessage } from '@/lib/apiErrorMessage.ts';
import type { Cohort } from '@/types/cohorts.ts';
import { getChangedFields } from '@/utils/getChangedFields.ts';
import { isoToDateInput } from '@/utils/dateUtils.ts';
import { formatPilotDate, pilotExplanation } from './pilotExplanation.ts';

const DEFAULT_PILOT_WEEKS = 6;

/** What the cohort form holds while it is being filled in: strings all the way down. */
interface CohortDraft {
  title: string;
  track_id: string;
  start_at: string;
  pilot: boolean;
  weeks: string;
  welcome_message: string;
}

const EMPTY_COHORT_DRAFT: CohortDraft = {
  title: '',
  track_id: '',
  start_at: '',
  pilot: false,
  weeks: String(DEFAULT_PILOT_WEEKS),
  welcome_message: '',
};

function draftFromCohort(cohort: Cohort): CohortDraft {
  return {
    title: cohort.title ?? '',
    track_id: cohort.track_id ?? '',
    start_at: isoToDateInput(cohort.start_at),
    pilot: cohort.pilot,
    weeks: String(cohort.weeks),
    welcome_message: cohort.welcome_message ?? '',
  };
}

/** An empty optional field is "no value", not "leave it as it was". */
function payloadFrom(draft: CohortDraft, organisationId: string): CohortWriteRequest {
  return {
    title: draft.title,
    organisation_id: organisationId,
    track_id: draft.track_id || null,
    start_at: draft.start_at || null,
    pilot: draft.pilot,
    weeks: Number(draft.weeks) || DEFAULT_PILOT_WEEKS,
    welcome_message: draft.welcome_message || null,
  };
}

export interface CohortFormProps {
  /** The cohort being edited, or undefined when one is being created. */
  cohort?: Cohort;
  /** The organisation the new cohort belongs to. */
  organisationId: string | null;
  /** Preselected track, from `?track_id=` on the create route. */
  initialTrackId?: string;
}

/** The one form behind both `/cohorts/create` and `/cohorts/:id/edit`. */
export function CohortForm({ cohort, organisationId, initialTrackId }: CohortFormProps) {
  const router = useRouter();
  const editing = cohort !== undefined;

  const [draft, setDraft] = useState<CohortDraft>(
    cohort
      ? draftFromCohort(cohort)
      : { ...EMPTY_COHORT_DRAFT, track_id: initialTrackId ?? '' },
  );
  const update = (patch: Partial<CohortDraft>) => setDraft({ ...draft, ...patch });

  const owner = cohort?.organisation_id ?? organisationId;
  const { error, submit, saving } = useCohortSave(cohort, owner);

  const tracks = useQuery({
    queryKey: ['tracks', 'options', owner],
    queryFn: () => listCohortTrackOptions(owner!),
    enabled: owner !== null,
  });

  const trackOptions = [
    { value: '', label: 'No track' },
    ...(tracks.data ?? []).map((track) => ({ value: track.id, label: track.title })),
  ];

  const loadFailed = tracks.isError ? 'Failed to load tracks' : '';
  const message = error || loadFailed;

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    submit(draft);
  }

  return (
    <>
      <PageHeader title={editing ? 'Edit Cohort' : 'Create Cohort'} />
      <ReadingColumn className='mt-13.5 flex flex-col gap-5'>
        <Link to='/cohorts' className={backLinkVariants()}>
          <ChevronLeft aria-hidden='true' />
          Cohorts
        </Link>

        <p className='text-base text-ink-muted'>
          {editing
            ? 'Update cohort information and settings'
            : 'Create a new cohort for your organisation'}
        </p>

        <Card>
          <form onSubmit={handleSubmit} className='flex flex-col gap-5'>
            {message !== '' && (
              <p
                role='alert'
                className='rounded-sm bg-danger-fill px-3.5 py-2.5 text-sm font-semibold text-danger'
              >
                {message}
              </p>
            )}

            <Field label='Title' required>
              {(control) => (
                <Input
                  {...control}
                  required
                  maxLength={TRACK_LIMITS.title}
                  value={draft.title}
                  onChange={(event) => update({ title: event.target.value })}
                />
              )}
            </Field>

            <Field
              label='Welcome Message'
              description={
                <>
                  This message will be shown to participants when they join the cohort.
                  Required: the welcome message must include that Wundo uses AI to
                  generate its responses (e.g. &lsquo;Wundo uses AI to generate its
                  responses.&rsquo;).
                </>
              }
            >
              {(control) => (
                <Textarea
                  {...control}
                  rows={4}
                  sizing='manual'
                  placeholder='Enter a welcome message for participants (optional)'
                  value={draft.welcome_message}
                  onChange={(event) => update({ welcome_message: event.target.value })}
                />
              )}
            </Field>

            <Field
              label='Track'
              description='Optional. A track adds teaching material, a welcome skill and a home skill.'
            >
              {(control) => (
                <Select
                  {...control}
                  wrapperClassName='w-full'
                  className='w-full'
                  options={trackOptions}
                  value={draft.track_id}
                  onChange={(event) => update({ track_id: event.target.value })}
                />
              )}
            </Field>

            <CohortPilotFields draft={draft} onChange={update} cohort={cohort} />

            <div className='flex justify-end gap-2'>
              <Button
                type='button'
                variant='quiet'
                disabled={saving}
                onClick={() => router.history.back()}
              >
                Cancel
              </Button>
              <Button type='submit' loading={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </form>
        </Card>
      </ReadingColumn>
    </>
  );
}

/**
 * Saving the cohort form. Editing sends only what changed, and a save with
 * nothing changed is no request at all: it goes back to the cohort. The backend
 * keeps an absent field as it was and clears one sent as null.
 */
function useCohortSave(cohort: Cohort | undefined, owner: string | null) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const [error, setError] = useState('');

  const save = useMutation({
    mutationFn: async (body: Partial<CohortWriteRequest>) => {
      if (cohort) return updateCohort(cohort.id, body);
      return createCohort(body as CohortWriteRequest);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['cohorts'] });
      await router.navigate({ to: '/cohorts/$id', params: { id: saved.id } });
    },
    onError: (failure) => setError(apiErrorMessage(failure, 'Failed to save cohort')),
  });

  function submit(draft: CohortDraft) {
    setError('');

    if (owner === null) {
      setError('No active organization selected. Please select an organization first.');
      return;
    }

    const payload = payloadFrom(draft, owner);

    if (cohort) {
      const changed = getChangedFields(
        payloadFrom(draftFromCohort(cohort), cohort.organisation_id) as unknown as Record<
          string,
          unknown
        >,
        payload as unknown as Record<string, unknown>,
      );

      if (Object.keys(changed).length === 0) {
        void router.navigate({ to: '/cohorts/$id', params: { id: cohort.id } });
        return;
      }

      save.mutate(changed as Partial<CohortWriteRequest>);
      return;
    }

    save.mutate(payload);
  }

  return { error, submit, saving: save.isPending };
}

interface CohortPilotFieldsProps {
  draft: CohortDraft;
  onChange: (patch: Partial<CohortDraft>) => void;
  /** The cohort being edited; undefined while one is created. */
  cohort: Cohort | undefined;
}

function CohortPilotFields({ draft, onChange, cohort }: CohortPilotFieldsProps) {
  /* A running pilot's members already have mails on the way, so its calendar is settled. */
  const startedOn = cohort?.pilot_timeline?.first_mail_on;
  const lockedNote =
    startedOn !== undefined && todayInBrussels() >= startedOn
      ? `Locked: this pilot started on ${formatPilotDate(startedOn)}.`
      : null;
  const locked = lockedNote !== null;

  const weeks = Number(draft.weeks);
  const explanation =
    draft.pilot && draft.start_at !== '' && weeks >= 1
      ? pilotExplanation(draft.start_at, weeks)
      : null;

  return (
    <>
      <Field
        label='Pilot'
        description='A pilot mails its members a practice every week and then ends.'
      >
        {(control) => (
          <Switch
            {...control}
            aria-label='Pilot'
            checked={draft.pilot}
            disabled={locked}
            onCheckedChange={(checked) => onChange({ pilot: checked })}
          />
        )}
      </Field>

      {lockedNote !== null && <p className='text-sm text-ink-muted'>{lockedNote}</p>}

      <Field
        label='Start Date'
        required={draft.pilot}
        description={
          draft.pilot
            ? 'Required. A pilot needs a start date: its first week is the first Monday on or after it.'
            : 'Optional. When the cohort starts.'
        }
      >
        {(control) => (
          <Input
            {...control}
            type='date'
            required={draft.pilot}
            disabled={locked}
            value={draft.start_at}
            onChange={(event) => onChange({ start_at: event.target.value })}
          />
        )}
      </Field>

      {draft.pilot && (
        <Field label='Weeks' description='How many weekly practice mails a member gets.'>
          {(control) => (
            <Input
              {...control}
              type='number'
              min={1}
              max={TRACK_LIMITS.pilotWeeks}
              required
              disabled={locked}
              value={draft.weeks}
              onChange={(event) => onChange({ weeks: event.target.value })}
            />
          )}
        </Field>
      )}

      {explanation !== null && <p className='text-sm text-ink-muted'>{explanation}</p>}
    </>
  );
}
